if (!customElements.get('artwork-viewer')) {
  customElements.define(
    'artwork-viewer',
    class ArtworkViewer extends HTMLElement {
      constructor() {
        super();
        this._unsub = null;
      }

      connectedCallback() {
        const sid = this.dataset.sectionId;
        const vEl = document.getElementById(`ArtworkViewerVariants-${sid}`);
        const namesEl = document.getElementById(`ArtworkViewerOptionNames-${sid}`);
        const framesEl = document.getElementById(`ArtworkViewerFrames-${sid}`);
        try {
          this._variants = vEl ? JSON.parse(vEl.textContent) : [];
          this._optionNames = namesEl ? JSON.parse(namesEl.textContent) : [];
          this._frames = this.parseFrames(framesEl);
        } catch (e) {
          this._variants = [];
          this._optionNames = [];
          this._frames = {};
        }

        this._overlay = this.querySelector('.product-artwork-viewer__overlay');
        this._fallbackOverlaySrc = this._overlay?.getAttribute('src') || '';
        this._slot = this.querySelector('.product-artwork-viewer__slot');
        this._frameMount = this.querySelector('.product-artwork-viewer__frame');
        this._imageEl = this.querySelector('.product-artwork-viewer__image');
        this._frameLoadCache = new Map();
        this._overlaySwapToken = 0;
        this._variantSwapToken = 0;
        this._frameCandidateCache = new Map();
        this._noFrameCandidateCache = new Map();
        this._frameCandidateCacheMax = 240;
        this._noFrameCandidateCacheMax = 160;
        this._assetsBase = this.dataset.assetsBase || '';
        this._frameAssetsVersion = (this.dataset.frameAssetsVersion || '').toString().trim();
        this._defaultFrameKey = this.slugify(this.dataset.defaultFrameKey || '');
        this._slotDefaults = {
          w: this.dataset.slotW || '56.5%',
          h: this.dataset.slotH || '84%',
          x: this.dataset.slotX || '50%',
          y: this.dataset.slotY || '50%',
          scale: this.dataset.slotScale || '1',
        };
        /* Cart previews must never inherit PDP form controls (frame / mount radios). */
        const inCartUi =
          typeof this.closest === 'function' &&
          Boolean(
            this.closest('#CartDrawer') ||
              this.closest('#main-cart-items') ||
              this.closest('cart-drawer')
          );
        /* Line-item mini viewers: outer CSS already scales the <artwork-viewer>; inner --artwork-mockup-scale was
           ~1.55 and effectively cancelled small transform tweaks — keep inner scale ≈1 so cart CSS controls the mock-up. */
        const inCartLineThumb =
          typeof this.closest === 'function' &&
          Boolean(
            this.closest('.cart-drawer-line__thumb-scale') ||
              this.closest('.cart-main-line__thumb-scale')
          );
        this._rootContext = inCartUi ? this : this.closest('product-info') || this;
        const mb = parseFloat(String(this.dataset.mockupSizeBoost ?? '').trim(), 10);
        let boostCap;
        let fallbackBoost;
        let minMb = 1;
        if (inCartLineThumb) {
          boostCap = 1.1;
          fallbackBoost = 1;
          minMb = 0.88;
        } else {
          boostCap = inCartUi ? 1.65 : 1.45;
          fallbackBoost = inCartUi ? 1.55 : 1.28;
        }
        this._mockupSizeBoost =
          !Number.isNaN(mb) && mb >= minMb && mb <= boostCap ? mb : fallbackBoost;
        this.syncMockupPresentationScale();

        const initialIdRaw = this.dataset.initialVariantId;
        const initialId = initialIdRaw != null ? parseInt(String(initialIdRaw), 10) : NaN;
        const initial =
          this._variants.find((v) =>
            Number.isFinite(initialId) ? this.variantIdsEqual(v.id, initialId) : false
          ) || this._variants[0];
        if (initial) {
          /* Drawer / mini cart thumbnails use scaled CSS keyed off data-frame-orientation; tune attribute before paint. */
          this.syncMiniThumbOrientationBeforeApply(initial);
          this.applyVariant(initial);
        }

        this.preloadInitialFrameImage();
        this._onArtworkImageLoad = () => {
          if (this._activeVariant) this.updateOverlayFrame(this._activeVariant, this._activeRow);
        };
        this._imageEl?.addEventListener('load', this._onArtworkImageLoad);

        this._onMountChange = (event) => {
          const t = event?.target;
          if (!t || !this._rootContext.contains(t)) return;
          if (t.type !== 'radio' && t.type !== 'checkbox' && t.tagName !== 'SELECT') return;
          if (this._activeVariant) this.updateOverlayFrame(this._activeVariant, this._activeRow);
        };
        this._rootContext.addEventListener('change', this._onMountChange);

        this._unsub = this.subscribeToVariantChanges();
      }

      disconnectedCallback() {
        if (this._unsub) this._unsub();
        if (this._onMountChange && this._rootContext) this._rootContext.removeEventListener('change', this._onMountChange);
        this._imageEl?.removeEventListener('load', this._onArtworkImageLoad);
      }

      subscribeToVariantChanges() {
        const eventName = 'variant-change';
        const handler = (payload) => {
          const { sectionId, variant } = payload?.data || {};
          if (!variant || String(sectionId) !== String(this.dataset.sectionId)) return;
          this.applyVariant(variant);
        };

        const subscribeFn =
          typeof globalThis !== 'undefined' && typeof globalThis.subscribe === 'function'
            ? globalThis.subscribe
            : null;

        const unsub = subscribeFn ? subscribeFn.call(globalThis, eventName, handler) : null;

        if (!unsub && typeof queueMicrotask === 'function') {
          queueMicrotask(() => {
            if (!this.isConnected) return;
            const retry =
              typeof globalThis !== 'undefined' && typeof globalThis.subscribe === 'function'
                ? globalThis.subscribe
                : null;
            if (!retry || this._unsub) return;
            this._unsub = retry.call(globalThis, eventName, handler);
          });
        }

        return unsub;
      }

      /** Cart / drawer mini viewers must ignore radios elsewhere on the page (recommendations, other products). */
      isCartLineArtworkViewer() {
        if (typeof this.closest !== 'function') return false;
        return Boolean(
          this.closest('#main-cart-items') || this.closest('#CartDrawer') || this.closest('cart-drawer')
        );
      }

      normalizeVariantId(value) {
        if (value == null || value === '') return null;
        const n = typeof value === 'string' ? parseInt(value, 10) : Number(value);
        return Number.isFinite(n) ? n : null;
      }

      variantIdsEqual(a, b) {
        if (a == null || b == null) return false;
        return String(a) === String(b) || this.normalizeVariantId(a) === this.normalizeVariantId(b);
      }

      findVariantRow(variant) {
        const vid = variant?.id;
        if (vid == null) return undefined;
        return this._variants.find((v) => this.variantIdsEqual(v.id, vid));
      }

      extractVariantPreviewSrc(variant) {
        const fm = variant?.featured_media;
        const pi = fm?.preview_image;
        if (typeof pi === 'string' && pi) return pi;
        if (pi?.src) return pi.src;
        return '';
      }

      syncMiniThumbOrientationBeforeApply(variant) {
        if (!variant || typeof this.closest !== 'function') return;
        const thumb =
          this.closest('.cart-drawer-line__thumb-scale') || this.closest('.cart-main-line__thumb-scale');
        if (!thumb) return;

        const row = this.findVariantRow(variant);
        const merged = row ? this.mergeRowWithVariant(row, variant) : variant;
        const ao = merged?.artwork_orientation ?? row?.artwork_orientation;
        const v = this.normalizeOrientationToken(String(ao ?? ''));
        if (!v || v === 'vertical') return;
        this.syncDataFrameOrientationAttribute(v);
      }

      mergeRowWithVariant(row, variant) {
        if (row) {
          if (row.featured_src) return row;
          const src = this.extractVariantPreviewSrc(variant);
          return src ? { ...row, featured_src: src } : row;
        }
        if (!variant) return undefined;
        return {
          id: variant.id,
          option1: variant.option1 ?? '',
          option2: variant.option2 ?? '',
          option3: variant.option3 ?? '',
          featured_src: this.extractVariantPreviewSrc(variant) || '',
        };
      }

      applyVariant(variant) {
        const row = this.findVariantRow(variant);
        const effectiveRow = this.mergeRowWithVariant(row, variant);
        const swapToken = ++this._variantSwapToken;
        this._activeVariant = variant;
        this._activeRow = effectiveRow;
        const fm = variant.featured_media;
        const fromVariant =
          fm && (fm.preview_image?.src || (typeof fm.preview_image === 'string' ? fm.preview_image : null));
        const src = effectiveRow?.featured_src || fromVariant;
        const img = this._imageEl || this.querySelector('.product-artwork-viewer__image');
        this.swapArtworkImageWhenReady(img, src, fm, swapToken, variant, effectiveRow);
        this.syncCartBwPreviewFilter();
        this.preloadLikelyAdjacentArtworkImages(effectiveRow);
      }

      async swapArtworkImageWhenReady(img, src, featuredMedia, swapToken, variant, row) {
        if (!img || !src) {
          await Promise.resolve(this.updateOverlayFrame(variant, row));
          return;
        }
        const currentSrc = img.getAttribute('src') || '';
        if (currentSrc === src) {
          await Promise.resolve(this.updateOverlayFrame(variant, row));
          img.classList.remove('is-swapping');
          this.applyArtworkImageDimensions(img, featuredMedia);
          return;
        }

        img.classList.add('is-swapping');
        const loadedSrc = await this.preloadImage(src);
        if (swapToken !== this._variantSwapToken) return;
        if (!loadedSrc) {
          img.classList.remove('is-swapping');
          return;
        }

        const overlaySwapPromise = Promise.resolve(this.updateOverlayFrame(variant, row));
        if (img.getAttribute('src') !== src) img.setAttribute('src', src);
        this.applyArtworkImageDimensions(img, featuredMedia);

        try {
          await Promise.all([
            overlaySwapPromise,
            typeof img.decode === 'function' ? img.decode().catch(() => null) : Promise.resolve(),
          ]);
        } catch (e) {
          // keep swap resilient even if decode/overlay lookup fails
        }
        if (swapToken !== this._variantSwapToken) return;

        requestAnimationFrame(() => {
          if (swapToken !== this._variantSwapToken) return;
          img.classList.remove('is-swapping');
        });
      }

      applyArtworkImageDimensions(img, featuredMedia) {
        const pw = featuredMedia?.preview_image?.width;
        const ph = featuredMedia?.preview_image?.height;
        if (pw && ph) {
          img.width = pw;
          img.height = ph;
        }
      }

      getAdjacentSizeTokens(sizeToken) {
        const order = ['x-small', 'small', 'medium', 'large', 'x-large', 'collector'];
        const normalized = this.normalizeSizeToken(sizeToken || '');
        const idx = order.indexOf(normalized);
        if (idx < 0) return [];
        const out = [];
        if (idx > 0) out.push(order[idx - 1]);
        if (idx < order.length - 1) out.push(order[idx + 1]);
        return out;
      }

      preloadLikelyAdjacentArtworkImages(activeRow) {
        if (!activeRow) return;
        const sizeOptionPosition = this.getSizeOptionPosition();
        if (!sizeOptionPosition) return;
        const currentSizeValue = (activeRow[`option${sizeOptionPosition}`] || '').toString();
        const currentSizeToken = this.normalizeSizeToken(currentSizeValue);
        if (!currentSizeToken) return;
        const adjacent = this.getAdjacentSizeTokens(currentSizeToken);
        if (!adjacent.length) return;

        const normalizeOption = (value) => (value || '').toString().trim().toLowerCase();
        const targetRows = [];
        for (const row of this._variants || []) {
          let sameOtherOptions = true;
          for (let i = 1; i <= 3; i += 1) {
            if (i === sizeOptionPosition) continue;
            if (normalizeOption(row?.[`option${i}`]) !== normalizeOption(activeRow?.[`option${i}`])) {
              sameOtherOptions = false;
              break;
            }
          }
          if (!sameOtherOptions) continue;
          const rowSizeToken = this.normalizeSizeToken((row?.[`option${sizeOptionPosition}`] || '').toString());
          if (!adjacent.includes(rowSizeToken)) continue;
          targetRows.push(row);
        }

        targetRows.forEach((row) => {
          const src = (row?.featured_src || '').toString().trim();
          if (src) this.preloadImage(src);
        });
      }

      getSizeOptionPosition() {
        const names = this._optionNames || [];
        for (let i = 0; i < names.length; i += 1) {
          const n = (names[i] || '').toString().toLowerCase();
          if (n.includes('size')) return i + 1;
        }
        return 0;
      }

      syncCartBwPreviewFilter() {
        const inCartDrawer =
          typeof this.closest === 'function' &&
          Boolean(
            this.closest('#CartDrawer') ||
              this.closest('cart-drawer') ||
              this.closest('#main-cart-items')
          );
        const apply = () => {
          const img = this._imageEl || this.querySelector('.product-artwork-viewer__image');
          const slot = this._slot || this.querySelector('.product-artwork-viewer__slot');
          const slotEl = slot instanceof Element ? slot : null;
          const clearSlot = () => {
            if (!slotEl) return;
            slotEl.style.removeProperty('filter');
            slotEl.style.removeProperty('-webkit-filter');
          };
          const clearImg = () => {
            if (!img) return;
            img.style.removeProperty('filter');
            img.style.removeProperty('-webkit-filter');
          };
          if (!inCartDrawer) {
            clearSlot();
            clearImg();
            return;
          }

          const markerRaw = (this.dataset.cartBwMarker || 'b&w').toString().trim();
          const marker = markerRaw.toLowerCase();
          let on =
            this.dataset.cartLineBw === 'true' ||
            (typeof this.classList !== 'undefined' && this.classList.contains('product-artwork-viewer--cart-bw')) ||
            this.dataset.cartBwPreview === 'true';

          const normCompact = (s) =>
            String(s || '')
              .toLowerCase()
              .replace(/\s+/g, '');

          if (!on && marker) {
            const vals = [
              this._activeRow?.option1,
              this._activeRow?.option2,
              this._activeRow?.option3,
              this._activeVariant?.option1,
              this._activeVariant?.option2,
              this._activeVariant?.option3,
            ]
              .filter(Boolean)
              .map((s) => String(s).toLowerCase());
            const markerCompact = normCompact(markerRaw.replace(/&amp;/g, '&'));
            on = vals.some((v) => {
              if (!marker.length) return false;
              if (v.includes(marker)) return true;
              return normCompact(v).includes(markerCompact);
            });
          }

          // Intentionally avoid fallback text/blob parsing in cart rows.
          // Option labels can include "B&W" even when the selected value is Colour.
          // We only trust explicit line flags + selected option values above.

          if (on) {
            clearSlot();
            if (img) {
              img.style.setProperty('filter', 'grayscale(1)', 'important');
              img.style.setProperty('-webkit-filter', 'grayscale(1)', 'important');
            }
            if (inCartDrawer) this.classList.add('product-artwork-viewer--cart-bw');
          } else {
            clearSlot();
            clearImg();
            if (inCartDrawer && this.dataset.cartLineBw !== 'true') {
              this.classList.remove('product-artwork-viewer--cart-bw');
            }
          }
        };

        if (inCartDrawer) {
          const run = () => {
            if (!this.isConnected) return;
            apply();
          };
          requestAnimationFrame(run);
          setTimeout(run, 0);
          setTimeout(run, 80);
        } else {
          apply();
        }
      }

      updateOverlayFrame(variant, row) {
        const noFrameSelected = this.isNoFrameSelected(variant, row);
        this.syncMountOptionState(noFrameSelected, this.isSignatureFloatFrameSelected(variant, row));

        const overlay = this._overlay || this.querySelector('.product-artwork-viewer__overlay');
        if (!overlay) return Promise.resolve();

        if (noFrameSelected) {
          return this.applyNoFramePreview(overlay, variant, row);
        }

        const frameKey = this.buildFrameKey(variant, row);
        const frameEntries = this._frames ? Object.keys(this._frames) : [];
        const hasSingleFrame = frameEntries.length === 1;
        const sizeHint =
          (this.isCartLineArtworkViewer() ? '' : this.getCurrentSizeToken()) ||
          this.extractSizeHint(frameKey);
        const colorHint = this.extractColorHint(frameKey) || 'white';
        const orientationHint = this.extractOrientationHint(frameKey) || 'vertical';
        this.syncDataFrameOrientationAttribute(orientationHint);
        const variantStyleMountHint = this.getFrameStyleMountHintFromVariant(variant, row);
        const styleMountHint = this.isCartLineArtworkViewer()
          ? ''
          : this.getCurrentFrameStyleMountHint();
        const uiMountSlug = this.normalizeMountValueToSlug(this.getCustomMountValue());
        const mountHint =
          uiMountSlug ||
          variantStyleMountHint ||
          styleMountHint ||
          this.extractMountHint(frameKey) ||
          'full-bleed';
        /** Signature Float uses full-bleed assets but slot tuning should match Gallery Frame + Full Bleed (border adjustments first). */
        const adjustmentStyleMountHint =
          this.isSignatureFloatFrameSelected(variant, row) && mountHint === 'full-bleed'
            ? 'border'
            : styleMountHint;
        const mountedFrameKey = this.replaceMountInFrameKey(frameKey, mountHint) || frameKey;
        const familyPrefix = `${orientationHint}-${colorHint}-${mountHint}-`;
        const explicitSizeKeyCandidate =
          sizeHint ? `${orientationHint}-${colorHint}-${mountHint}-${sizeHint}` : '';
        const explicitSizeKey = explicitSizeKeyCandidate && this._frames?.[explicitSizeKeyCandidate] ? explicitSizeKeyCandidate : '';
        const collectorFallbackKey =
          sizeHint === 'collector'
            ? frameEntries.find(
                (k) => k.startsWith(familyPrefix) && (k.endsWith('-collector') || k.includes('-collector-'))
              )
            : '';
        const sizeFallbackKey =
          sizeHint && frameEntries.length
            ? frameEntries.find(
                (k) => k.startsWith(familyPrefix) && (k.endsWith(`-${sizeHint}`) || k.includes(`-${sizeHint}-`))
              )
            : '';
        const exactFrameKey =
          (explicitSizeKey ? explicitSizeKey : '') || (this._frames?.[mountedFrameKey] ? mountedFrameKey : '');
        const lockToFrameFamily =
          orientationHint === 'horizontal-3-2' || orientationHint === 'vertical-2-3';
        const fallbackFrameKey =
          exactFrameKey ||
          collectorFallbackKey ||
          sizeFallbackKey ||
          frameEntries.find(
            (k) =>
              (lockToFrameFamily
                ? k.startsWith(`${orientationHint}-`)
                : (k.startsWith('vertical-') || k.startsWith('horizontal-') || k.startsWith('square-'))) &&
              k.includes(`-${colorHint}-${mountHint}-`) &&
              (k.endsWith(`-${sizeHint}`) || (sizeHint === 'collector' && k.endsWith('-collector')))
          ) ||
          frameEntries.find(
            (k) =>
              (lockToFrameFamily
                ? k.startsWith(`${orientationHint}-`)
                : (k.startsWith('vertical-') || k.startsWith('horizontal-') || k.startsWith('square-'))) &&
              k.includes(`-${colorHint}-${mountHint}-`)
          ) ||
          (!lockToFrameFamily && this._defaultFrameKey && this._frames?.[this._defaultFrameKey] ? this._defaultFrameKey : '') ||
          (hasSingleFrame ? frameEntries[0] : '');
        const frameCfgRaw = fallbackFrameKey ? this._frames?.[fallbackFrameKey] : null;
        const frameCfg =
          typeof frameCfgRaw === 'string'
            ? { src: frameCfgRaw }
            : frameCfgRaw && typeof frameCfgRaw === 'object'
              ? frameCfgRaw
              : {};
        const rawSrc = typeof frameCfg.src === 'string' ? frameCfg.src : '';
        const primaryGeneratedCandidates = this.buildFrameAssetUrlCandidates(mountedFrameKey);
        const alternateMount = mountHint === 'border' ? 'full-bleed' : 'border';
        const alternateMountFrameKey = this.replaceMountInFrameKey(mountedFrameKey, alternateMount);
        const alternateMountCandidates =
          alternateMountFrameKey && alternateMountFrameKey !== mountedFrameKey
            ? this.buildFrameAssetUrlCandidates(alternateMountFrameKey)
            : [];
        const squareSafetyCandidates =
          orientationHint === 'square' && this._assetsBase
            ? (() => {
                const squareMount = mountHint === 'border' ? 'border' : 'full-bleed';
                const oppositeMount = squareMount === 'border' ? 'full-bleed' : 'border';
                const sizeToken = sizeHint || 'collector';
                const mountBleedUnderscore = squareMount.replace('full-bleed', 'full_bleed');
                const oppositeBleedUnderscore = oppositeMount.replace('full-bleed', 'full_bleed');
                return [
                  `${this._assetsBase}square_${colorHint}_${squareMount}_${sizeToken}.webp`,
                  `${this._assetsBase}square_${colorHint}_${mountBleedUnderscore}_${sizeToken}.webp`,
                  `${this._assetsBase}square_white_${squareMount}_${sizeToken}.webp`,
                  `${this._assetsBase}square_white_${mountBleedUnderscore}_${sizeToken}.webp`,
                  `${this._assetsBase}square_white_${mountBleedUnderscore}_collector.webp`,
                  `${this._assetsBase}square_white_${squareMount}_collector.webp`,
                  `${this._assetsBase}square_${colorHint}_${oppositeMount}_${sizeToken}.webp`,
                  `${this._assetsBase}square_${colorHint}_${oppositeBleedUnderscore}_${sizeToken}.webp`,
                  `${this._assetsBase}square_white_${oppositeMount}_${sizeToken}.webp`,
                  `${this._assetsBase}square_white_${oppositeBleedUnderscore}_${sizeToken}.webp`,
                  `${this._assetsBase}square_white_${oppositeBleedUnderscore}_collector.webp`,
                ];
              })().map((url) => this.withFrameAssetVersion(url))
            : [];
        const fallbackGeneratedCandidates =
          fallbackFrameKey && fallbackFrameKey !== mountedFrameKey ? this.buildFrameAssetUrlCandidates(fallbackFrameKey) : [];
        const hasExactMappedFrame = Boolean(exactFrameKey);
        const srcCandidates = hasExactMappedFrame
          ? [rawSrc, ...primaryGeneratedCandidates, ...alternateMountCandidates, ...squareSafetyCandidates, ...fallbackGeneratedCandidates]
          : [...primaryGeneratedCandidates, ...alternateMountCandidates, ...squareSafetyCandidates, ...fallbackGeneratedCandidates, rawSrc];
        const overlaySwapPromise = this.setOverlaySrcFromCandidates(overlay, srcCandidates);
        this.preloadLikelyAdjacentFrameAssets({
          orientation: orientationHint,
          color: colorHint,
          mount: mountHint,
          size: sizeHint || this.extractSizeHint(mountedFrameKey) || '',
        });

        if (this._slot) {
          const normalizedSize = this.normalizeSizeToken(sizeHint || 'collector');
          const framedSizeAdjustments = {
            square: {
              border: {
                collector: { w: -7.0, h: -7.0, x: 1.2, y: -0.4 },
                'x-large': { w: -13.0, h: -13.0, x: -0.15, y: -0.35 },
                xlarge: { w: -13.0, h: -13.0, x: -0.15, y: -0.35 },
                large: { w: -19.0, h: -19.0, x: 0.0, y: 0.40 },
                medium: { w: -26.0, h: -26.0, x: -0.1, y: 0.35 },
                small: { w: -30.0, h: -30.0, x: 0.2, y: 0.1 },
                'x-small': { w: -24.0, h: -24.0, x: -0.17, y: -0.08 },
              },
              'full-bleed': {
                collector: { w: -2.0, h: -2.0, x: -0.9, y: 0.2 },
                'x-large': { w: -6.0, h: -6.0, x: -0.4, y: 1.88 },
                xlarge: { w: -6.0, h: -6.0, x: -0.4, y: 1.88 },
                /* Square full-bleed large: w/h shrink slot; x/y are %-point deltas on base slot (left/top). */
                large: { w: -11.0, h: -11.0, x: -0.35, y: -1.64 },
                medium: { w: -16.0, h: -16.0, x: -0.1, y: 0.35 },
                small: { w: -22.0, h: -22.0, x: -0.4, y: -0.3 },
                'x-small': { w: -17.0, h: -17.0, x: -0.17, y: -0.08 },
              },
            },
          };
          const sizeLookupKeys = [normalizedSize];
          if (normalizedSize === 'x-large') sizeLookupKeys.push('xlarge', 'xl');
          if (normalizedSize === 'x-small') sizeLookupKeys.push('xsmall', 'xs');
          const orientationAdjustments = framedSizeAdjustments?.[orientationHint] || null;
          const mountLookupKeys = [...new Set([adjustmentStyleMountHint, mountHint].filter(Boolean))];
          let activeFramedAdjustments = null;
          for (const mountKey of mountLookupKeys) {
            const mountAdjustments = orientationAdjustments?.[mountKey] || null;
            for (const key of sizeLookupKeys) {
              if (mountAdjustments?.[key]) {
                activeFramedAdjustments = mountAdjustments[key];
                break;
              }
            }
            if (activeFramedAdjustments) break;
          }
          if (!activeFramedAdjustments && orientationHint === 'square' && framedSizeAdjustments?.square) {
            const mountsToTry = [
              ...new Set([mountHint, adjustmentStyleMountHint, 'full-bleed', 'border'].filter(Boolean)),
            ];
            const sizeFallbackKeys = [
              ...new Set(
                [normalizedSize, normalizedSize === 'x-large' ? 'xlarge' : null, 'collector'].filter(Boolean)
              ),
            ];
            outer: for (const mk of mountsToTry) {
              const byMount = framedSizeAdjustments.square[mk];
              for (const sk of sizeFallbackKeys) {
                if (byMount?.[sk]) {
                  activeFramedAdjustments = byMount[sk];
                  break outer;
                }
              }
            }
          }
          const baseSlotW = frameCfg.slot_w || this._slotDefaults.w;
          const baseSlotH = frameCfg.slot_h || this._slotDefaults.h;
          const baseSlotX = frameCfg.slot_x || this._slotDefaults.x;
          const baseSlotY = frameCfg.slot_y || this._slotDefaults.y;
          let slotW = activeFramedAdjustments
            ? this.adjustPercentValue(baseSlotW, activeFramedAdjustments.w)
            : baseSlotW;
          let slotH = activeFramedAdjustments
            ? this.adjustPercentValue(baseSlotH, activeFramedAdjustments.h)
            : baseSlotH;
          const frameFamilyOrientation = this.extractFrameFamilyOrientationToken(
            this.dataset.frameFamilyOrientation || orientationHint
          );
          const isHorizontalThreeTwoFamily = frameFamilyOrientation === 'horizontal-3-2';
          const isVerticalTwoThreeFamily = frameFamilyOrientation === 'vertical-2-3';
          const isVerticalTwoThreeEffective =
            isVerticalTwoThreeFamily ||
            orientationHint === 'vertical-2-3' ||
            String(mountedFrameKey || '').includes('vertical-2-3') ||
            String(fallbackFrameKey || '').includes('vertical-2-3');
          if (isHorizontalThreeTwoFamily && normalizedSize === 'collector') {
            slotW = this.adjustPercentValue(slotW, 1.4);
            slotH = this.adjustPercentValue(slotH, 1.4);
          }
          if (isVerticalTwoThreeEffective) {
            slotW = this.adjustPercentValue(slotW, 0.9);
            slotH = this.adjustPercentValue(slotH, 0.9);
          }
          if (isVerticalTwoThreeEffective && normalizedSize === 'collector') {
            slotW = this.adjustPercentValue(slotW, 2.2);
            slotH = this.adjustPercentValue(slotH, 2.2);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'border' &&
            normalizedSize === 'collector'
          ) {
            slotW = this.adjustPercentValue(slotW, 7.5);
            slotH = this.adjustPercentValue(slotH, 7.5);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'full-bleed' &&
            normalizedSize === 'collector'
          ) {
            slotW = this.adjustPercentValue(slotW, 1.0);
            slotH = this.adjustPercentValue(slotH, 1.0);
          }
          if (isVerticalTwoThreeEffective && normalizedSize === 'large') {
            slotW = this.adjustPercentValue(slotW, -10.8);
            slotH = this.adjustPercentValue(slotH, -10.8);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'border' &&
            normalizedSize === 'large'
          ) {
            slotW = this.adjustPercentValue(slotW, 31.0);
            slotH = this.adjustPercentValue(slotH, 31.0);
          }
          if (isVerticalTwoThreeEffective && normalizedSize === 'medium') {
            slotW = this.adjustPercentValue(slotW, -18.0);
            slotH = this.adjustPercentValue(slotH, -18.0);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'border' &&
            normalizedSize === 'medium'
          ) {
            slotW = this.adjustPercentValue(slotW, 35.5);
            slotH = this.adjustPercentValue(slotH, 35.5);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'border' &&
            normalizedSize === 'small'
          ) {
            slotW = this.adjustPercentValue(slotW, 20.5);
            slotH = this.adjustPercentValue(slotH, 20.5);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'full-bleed' &&
            normalizedSize === 'small'
          ) {
            slotW = this.adjustPercentValue(slotW, -23.5);
            slotH = this.adjustPercentValue(slotH, -23.5);
          }
          if (
            isVerticalTwoThreeEffective &&
            (normalizedSize === 'x-small' || normalizedSize === 'xsmall')
          ) {
            slotW = this.adjustPercentValue(slotW, -28.0);
            slotH = this.adjustPercentValue(slotH, -28.0);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'border' &&
            (normalizedSize === 'x-small' || normalizedSize === 'xsmall')
          ) {
            slotW = this.adjustPercentValue(slotW, 48.5);
            slotH = this.adjustPercentValue(slotH, 48.5);
          }
          if (
            isVerticalTwoThreeEffective &&
            (normalizedSize === 'x-large' || normalizedSize === 'xlarge')
          ) {
            slotW = this.adjustPercentValue(slotW, -3.6);
            slotH = this.adjustPercentValue(slotH, -3.6);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'border' &&
            (normalizedSize === 'x-large' || normalizedSize === 'xlarge')
          ) {
            slotW = this.adjustPercentValue(slotW, 16.0);
            slotH = this.adjustPercentValue(slotH, 16.0);
          }
          if (
            orientationHint === 'vertical' &&
            !isVerticalTwoThreeEffective &&
            mountHint === 'border' &&
            normalizedSize === 'small'
          ) {
            slotW = this.adjustPercentValue(slotW, -3.0);
            slotH = this.adjustPercentValue(slotH, -3.0);
          }
          if (
            orientationHint === 'vertical' &&
            !isVerticalTwoThreeEffective &&
            mountHint === 'border' &&
            (normalizedSize === 'x-small' || normalizedSize === 'xsmall')
          ) {
            slotW = this.adjustPercentValue(slotW, -3.5);
            slotH = this.adjustPercentValue(slotH, -3.5);
          }
          /* Cart line thumbnails only: square full-bleed openings in the PNG are tighter than PDP slot tuning; without extra shrink the print visibly spills past the frame edge at 10rem. */
          if (
            this.isCartLineArtworkViewer() &&
            orientationHint === 'square' &&
            mountHint === 'full-bleed'
          ) {
            let cartFbExtra = 7;
            if (normalizedSize === 'collector') cartFbExtra = 9;
            else if (normalizedSize === 'large') cartFbExtra = 8;
            else if (normalizedSize === 'medium' || normalizedSize === 'small') cartFbExtra = 7;
            else if (normalizedSize === 'x-large' || normalizedSize === 'xlarge') cartFbExtra = 7;
            else if (normalizedSize === 'x-small' || normalizedSize === 'xsmall') cartFbExtra = 7;
            slotW = this.adjustPercentValue(slotW, -cartFbExtra);
            slotH = this.adjustPercentValue(slotH, -cartFbExtra);
          }
          const slotX = activeFramedAdjustments
            ? this.adjustPercentValue(baseSlotX, activeFramedAdjustments.x)
            : baseSlotX;
          const slotYBase = activeFramedAdjustments
            ? this.adjustPercentValue(baseSlotY, activeFramedAdjustments.y)
            : baseSlotY;
          const slotXWithRatioAdjustment =
            isHorizontalThreeTwoFamily
              ? this.adjustPercentValue(slotX, 0.8)
              : slotX;
          const slotYWithRatioAdjustment =
            isHorizontalThreeTwoFamily
              ? this.adjustPercentValue(slotYBase, 0.8)
              : slotYBase;
          /* Cart drawer mini preview only: wood square full-bleed X‑Large PNG opening differs from white; do not change PDP / cart page. */
          const inCartDrawer = typeof this.closest === 'function' && Boolean(this.closest('#CartDrawer'));
          const cartDrawerWoodSquareFbXl =
            inCartDrawer &&
            orientationHint === 'square' &&
            mountHint === 'full-bleed' &&
            (normalizedSize === 'x-large' || normalizedSize === 'xlarge') &&
            (colorHint === 'wood' || colorHint === 'natural-wood');
          let slotXForPlacement = slotX;
          let slotYForPlacement = slotYWithRatioAdjustment;
          if (isHorizontalThreeTwoFamily) {
            slotXForPlacement = slotXWithRatioAdjustment;
            if (mountHint === 'border') {
              if (normalizedSize === 'collector') {
                slotXForPlacement = this.adjustPercentValue(slotXForPlacement, -0.9);
                slotYForPlacement = this.adjustPercentValue(slotYForPlacement, -0.8);
              } else if (normalizedSize === 'medium') {
                slotXForPlacement = this.adjustPercentValue(slotXForPlacement, -0.9);
                slotYForPlacement = this.adjustPercentValue(slotYForPlacement, -0.8);
              } else if (normalizedSize === 'small') {
                slotXForPlacement = this.adjustPercentValue(slotXForPlacement, -0.9);
                slotYForPlacement = this.adjustPercentValue(slotYForPlacement, -0.8);
              } else if (normalizedSize === 'x-small') {
                slotXForPlacement = this.adjustPercentValue(slotXForPlacement, -0.9);
                slotYForPlacement = this.adjustPercentValue(slotYForPlacement, -0.8);
              }
            } else if (mountHint === 'full-bleed') {
              const fullBleedPositionAdjustments = {
                collector: { x: -0.6, y: -0.55 },
                'x-large': { x: -0.6, y: -0.8 },
                xlarge: { x: -0.6, y: -0.8 },
                large: { x: -0.65, y: -0.75 },
                medium: { x: -0.7, y: -0.75 },
                small: { x: -0.75, y: -0.75 },
                'x-small': { x: -0.8, y: -0.75 },
                xsmall: { x: -0.8, y: -0.75 },
              };
              const posAdjustment = fullBleedPositionAdjustments[normalizedSize];
              if (posAdjustment) {
                slotXForPlacement = this.adjustPercentValue(slotXForPlacement, posAdjustment.x);
                slotYForPlacement = this.adjustPercentValue(slotYForPlacement, posAdjustment.y);
              }
            }
          }
          if (isVerticalTwoThreeEffective && normalizedSize === 'collector') {
            slotYForPlacement = this.adjustPercentValue(slotYForPlacement, 2.0);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'border' &&
            normalizedSize === 'collector'
          ) {
            slotYForPlacement = this.adjustPercentValue(slotYForPlacement, -1.2);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'full-bleed' &&
            normalizedSize === 'collector'
          ) {
            slotXForPlacement = this.adjustPercentValue(slotXForPlacement, -0.9);
            slotYForPlacement = this.adjustPercentValue(slotYForPlacement, -0.5);
          }
          if (isVerticalTwoThreeEffective && normalizedSize === 'large') {
            slotXForPlacement = this.adjustPercentValue(slotXForPlacement, -0.9);
            slotYForPlacement = this.adjustPercentValue(slotYForPlacement, 1.2);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'border' &&
            normalizedSize === 'large'
          ) {
            slotYForPlacement = this.adjustPercentValue(slotYForPlacement, 1.0);
          }
          if (isVerticalTwoThreeEffective && normalizedSize === 'medium') {
            slotXForPlacement = this.adjustPercentValue(slotXForPlacement, -1.0);
            slotYForPlacement = this.adjustPercentValue(slotYForPlacement, 1.6);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'border' &&
            normalizedSize === 'medium'
          ) {
            slotXForPlacement = this.adjustPercentValue(slotXForPlacement, -0.5);
            slotYForPlacement = this.adjustPercentValue(slotYForPlacement, 0.6);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'border' &&
            normalizedSize === 'small'
          ) {
            slotXForPlacement = this.adjustPercentValue(slotXForPlacement, -1.9);
            slotYForPlacement = this.adjustPercentValue(slotYForPlacement, 2.2);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'full-bleed' &&
            normalizedSize === 'small'
          ) {
            slotXForPlacement = this.adjustPercentValue(slotXForPlacement, -1.0);
            slotYForPlacement = this.adjustPercentValue(slotYForPlacement, 1.8);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'full-bleed' &&
            (normalizedSize === 'x-small' || normalizedSize === 'xsmall')
          ) {
            slotW = this.adjustPercentValue(slotW, -1.0);
            slotH = this.adjustPercentValue(slotH, -1.0);
            slotXForPlacement = this.adjustPercentValue(slotXForPlacement, -1.2);
          }
          if (
            isVerticalTwoThreeEffective &&
            (normalizedSize === 'x-small' || normalizedSize === 'xsmall')
          ) {
            slotYForPlacement = this.adjustPercentValue(slotYForPlacement, 1.5);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'border' &&
            (normalizedSize === 'x-small' || normalizedSize === 'xsmall')
          ) {
            slotXForPlacement = this.adjustPercentValue(slotXForPlacement, -1.8);
            slotYForPlacement = this.adjustPercentValue(slotYForPlacement, 0.4);
          }
          if (
            isVerticalTwoThreeEffective &&
            (normalizedSize === 'x-large' || normalizedSize === 'xlarge')
          ) {
            slotXForPlacement = this.adjustPercentValue(slotXForPlacement, -1.0);
            slotYForPlacement = this.adjustPercentValue(slotYForPlacement, 1.9);
          }
          if (
            isVerticalTwoThreeEffective &&
            mountHint === 'border' &&
            (normalizedSize === 'x-large' || normalizedSize === 'xlarge')
          ) {
            slotYForPlacement = this.adjustPercentValue(slotYForPlacement, 0.4);
          }
          if (
            orientationHint === 'vertical' &&
            !isVerticalTwoThreeEffective &&
            mountHint === 'border' &&
            (normalizedSize === 'x-small' || normalizedSize === 'xsmall')
          ) {
            slotXForPlacement = this.adjustPercentValue(slotXForPlacement, 0.35);
            slotYForPlacement = this.adjustPercentValue(slotYForPlacement, -0.2);
          }
          if (cartDrawerWoodSquareFbXl) {
            slotXForPlacement = this.adjustPercentValue(slotX, -1.2);
            slotYForPlacement = this.adjustPercentValue(slotYBase, -1.92);
          }
          /* Cart only: wood square full-bleed Large — print reads above the frame lip; base large y-delta is tuned for white/black. */
          const cartWoodSquareFbLarge =
            this.isCartLineArtworkViewer() &&
            orientationHint === 'square' &&
            mountHint === 'full-bleed' &&
            normalizedSize === 'large' &&
            (colorHint === 'wood' || colorHint === 'natural-wood');
          if (cartWoodSquareFbLarge) {
            slotYForPlacement = this.adjustPercentValue(slotYForPlacement, 2.35);
          }
          /* Wood square border assets use the same slot tuning as white/black, but the mat opening is often
             shifted in the PNG — art reads “high” vs the frame; nudge slot down (higher top %) for wood only. */
          const woodSquareBorderSmallNudge =
            orientationHint === 'square' &&
            mountHint === 'border' &&
            normalizedSize === 'small' &&
            (colorHint === 'wood' || colorHint === 'natural-wood')
              ? 0.32
              : 0;
          const slotY =
            woodSquareBorderSmallNudge !== 0
              ? this.adjustPercentValue(slotYForPlacement, woodSquareBorderSmallNudge)
              : slotYForPlacement;
          this._slot.style.setProperty('--artwork-slot-w', slotW);
          this._slot.style.setProperty('--artwork-slot-h', slotH);
          this._slot.style.setProperty('--artwork-slot-x', slotXForPlacement);
          this._slot.style.setProperty('--artwork-slot-y', slotY);
          this._slot.style.setProperty(
            '--artwork-image-scale',
            (() => {
              let imageScale = frameCfg.image_scale || frameCfg.art_scale || this._slotDefaults.scale;
              if (isHorizontalThreeTwoFamily && mountHint === 'border') {
                if (normalizedSize === 'collector') {
                  const base = parseFloat(imageScale);
                  if (Number.isFinite(base)) {
                    imageScale = String(Math.round((base + 0.12) * 1000) / 1000);
                  }
                } else if (normalizedSize === 'large') {
                  const base = parseFloat(imageScale);
                  if (Number.isFinite(base)) {
                    imageScale = String(Math.max(0.01, Math.round((base - 0.12) * 1000) / 1000));
                  }
                } else if (normalizedSize === 'medium') {
                  const base = parseFloat(imageScale);
                  if (Number.isFinite(base)) {
                    imageScale = String(Math.max(0.01, Math.round((base - 0.26) * 1000) / 1000));
                  }
                } else if (normalizedSize === 'small') {
                  const base = parseFloat(imageScale);
                  if (Number.isFinite(base)) {
                    imageScale = String(Math.max(0.01, Math.round((base - 0.36) * 1000) / 1000));
                  }
                } else if (normalizedSize === 'x-small') {
                  const base = parseFloat(imageScale);
                  if (Number.isFinite(base)) {
                    imageScale = String(Math.max(0.01, Math.round((base - 0.46) * 1000) / 1000));
                  }
                }
              } else if (isHorizontalThreeTwoFamily && mountHint === 'full-bleed') {
                const base = parseFloat(imageScale);
                if (Number.isFinite(base)) {
                  const fullBleedScaleAdjustments = {
                    collector: 0.08,
                    'x-large': -0.03,
                    xlarge: -0.03,
                    large: -0.14,
                    medium: -0.26,
                    small: -0.37,
                    'x-small': -0.48,
                    xsmall: -0.48,
                  };
                  const scaleAdjustment = fullBleedScaleAdjustments[normalizedSize];
                  if (Number.isFinite(scaleAdjustment)) {
                    imageScale = String(Math.max(0.01, Math.round((base + scaleAdjustment) * 1000) / 1000));
                  }
                }
              }
              return imageScale;
            })()
          );
        }
        return overlaySwapPromise;
      }

      isNoFrameSelected(variant, row) {
        if (!this.isCartLineArtworkViewer()) {
          const frameStyleCheckedInput = this.getActiveCheckedInput(
            ".product-form__input--frame-style-list input[type='radio']:checked"
          );
          if (frameStyleCheckedInput) {
            const selectedValue = (frameStyleCheckedInput.value || '').toString().toLowerCase();
            const explicitNoFrameSelected =
              frameStyleCheckedInput.dataset.isNoFrame === 'true' ||
              selectedValue.includes('no frame') ||
              selectedValue.includes('print only') ||
              selectedValue.includes('unframed');

            return explicitNoFrameSelected;
          }
        }

        const optionValues = [
          (row?.option1 ?? variant?.option1 ?? '').toString().toLowerCase(),
          (row?.option2 ?? variant?.option2 ?? '').toString().toLowerCase(),
          (row?.option3 ?? variant?.option3 ?? '').toString().toLowerCase(),
        ];

        return optionValues.some(
          (value) =>
            value.includes('no frame') || value.includes('print only') || value.includes('unframed')
        );
      }

      applyNoFramePreview(overlay, variant, row) {
        if (!this._slot) {
          overlay.style.opacity = '0';
          return Promise.resolve();
        }

        const knownSizes = ['x-small', 'x-large', 'small', 'medium', 'large', 'collector'];
        const optionsBlob = [
          (row?.option1 ?? variant?.option1 ?? '').toString().toLowerCase(),
          (row?.option2 ?? variant?.option2 ?? '').toString().toLowerCase(),
          (row?.option3 ?? variant?.option3 ?? '').toString().toLowerCase(),
        ].join(' ');
        const fromImage = this.inferOrientationFromDisplayedImage();
        const forcedO = this.normalizeOrientationToken(this.dataset.frameOrientation || '');
        const variantO = this.normalizeOrientationToken(String(row?.artwork_orientation ?? variant?.artwork_orientation ?? ''));
        const optionO = this.normalizeOrientationToken(this.findToken(optionsBlob, ['square', 'horizontal', 'vertical']));
        const mediaO = this.inferOrientationFromVariantMedia(variant, row);
        const unframedOrientationCandidates = [];
        if (variantO) unframedOrientationCandidates.push(variantO);
        if (forcedO === 'square') unframedOrientationCandidates.push('square');
        if (forcedO === 'horizontal') unframedOrientationCandidates.push('horizontal');
        unframedOrientationCandidates.push(optionO, mediaO, fromImage);
        if (forcedO === 'vertical') unframedOrientationCandidates.push('vertical');
        unframedOrientationCandidates.push('vertical');
        let orientation = this.pickNoFrameOrientation(unframedOrientationCandidates);
        const frameFamilyOrientation = this.extractFrameFamilyOrientationToken(
          this.dataset.frameFamilyOrientation || ''
        );
        if (forcedO === 'square') {
          orientation = 'square';
        } else if (forcedO === 'horizontal') {
          orientation = 'horizontal';
        } else if (variantO === 'square') {
          orientation = 'square';
        } else if (variantO === 'horizontal') {
          orientation = 'horizontal';
        }
        if (frameFamilyOrientation === 'horizontal-3-2') {
          orientation = 'horizontal-3-2';
        } else if (frameFamilyOrientation === 'vertical-2-3') {
          orientation = 'vertical-2-3';
        }
        this.syncDataFrameOrientationAttribute(orientation);
        const sizeToken = this.getSizeTokenForUnframed(variant, row, optionsBlob, knownSizes);
        const size = sizeToken || 'collector';
        const normalizedSize = this.normalizeSizeToken(size);
        const noFrameCandidates = this.buildNoFrameAssetUrlCandidates(orientation, this.normalizeSizeToken(size));
        const overlaySwapPromise = this.setOverlayNoFrameFromCandidates(overlay, noFrameCandidates);
        this.preloadLikelyAdjacentNoFrameAssets(orientation, normalizedSize);
        let baselineCfg = this.getNoFrameBaselineSlotConfig(orientation, normalizedSize);
        /* Framed map may omit uncommon sizes — keep mat slot geometry aligned with real frame overlays. */
        if (!baselineCfg && orientation === 'square') {
          baselineCfg = { slot_w: '92%', slot_h: '92%', slot_x: '50%', slot_y: '50%', image_scale: '1' };
        }
        if (!baselineCfg && (orientation === 'horizontal' || orientation === 'horizontal-3-2')) {
          baselineCfg = {
            slot_w: '81%',
            slot_h: '57.1%',
            slot_x: '50%',
            slot_y: '50%',
            image_scale: '0.985',
          };
        }
        if (baselineCfg) {
          const sizeAdjustments = {
            collector: { w: 3.8, h: 3.8, x: -0.6, y: -1.2 },
            medium: { w: 9.5, h: 9.5, x: -1.4, y: 0.15 },
            small: { w: 12.5, h: 12.5, x: -2.0 },
            'x-small': { w: 12.0, h: 12.0, x: -2.0, y: 0.2 },
            large: { w: 0.8, h: 0.8, x: -1.8 },
            'x-large': { w: 7.4, h: 7.4, x: -1.9, y: -0.2 },
          };
          const orientationSizeAdjustments = {
            horizontal: {
              collector: { w: 7, h: 7, x: 0.2, y: -0.2 },
              medium: { w: 10.5, h: 10.5, x: -0.2, y: 0.1 },
              small: { w: 14.5, h: 14.5, x: 0.0, y: -0.4 },
              'x-small': { w: 14.0, h: 14.0, x: 0.0, y: -0.2 },
              large: { w: 0, h: 0, x: 0 },
              'x-large': { w: 6.4, h: 6.4, x: -0.45, y: -0.2 },
            },
            square: {
              collector: { w: -3.5, h: -3.5, x: 1.2, y: -0.8 },
              medium: { w: -27.5, h: -27.5, x: 0.25, y: 0.45 },
              /* No-frame square_small.webp: larger inner “paper” — tune slot shrink so print fills asset */
              small: { w: -31.0, h: -31.0, x: 0.15, y: 0 },
              'x-small': { w: -23.5, h: -23.5, x: 0.35, y: 0.15 },
              large: { w: -15.5, h: -15.5, x: -0.35, y: 1.7 },
              'x-large': { w: -12.9, h: -12.9, x: -0.45, y: -0.65 },
            },
          };
          const orientationForAdjustments =
            orientation === 'horizontal-3-2' ? 'horizontal' : orientation;
          let activeAdjustments = orientationSizeAdjustments?.[orientationForAdjustments]?.[normalizedSize] || null;
          if (!activeAdjustments && orientation === 'vertical') {
            activeAdjustments = sizeAdjustments[normalizedSize] || null;
          }
          if (!activeAdjustments && orientation === 'horizontal') {
            const ho = orientationSizeAdjustments.horizontal;
            const ns = normalizedSize;
            const tryKeys = [ns];
            if (ns === 'x-small' || ns === 'xsmall' || ns === 'xs') tryKeys.push('x-small', 'small');
            for (const k of tryKeys) {
              if (ho?.[k]) {
                activeAdjustments = ho[k];
                break;
              }
            }
            if (!activeAdjustments) activeAdjustments = ho?.collector ?? null;
          }
          if (orientation === 'square' && !activeAdjustments) {
            const sq = orientationSizeAdjustments.square;
            const ns = normalizedSize;
            const tryKeys = [ns];
            if (ns === 'x-small' || ns === 'xsmall' || ns === 'xs') tryKeys.push('x-small', 'xsmall', 'small');
            if (ns === 'small' || ns === 's') tryKeys.push('small');
            for (const k of tryKeys) {
              if (sq?.[k]) {
                activeAdjustments = sq[k];
                break;
              }
            }
            if (!activeAdjustments && ['mini', 'compact', 'petite', '2xs', 'xxs'].includes(ns)) {
              activeAdjustments = sq?.['x-small'] || sq?.small || null;
            }
          }
          let slotW = activeAdjustments
            ? this.adjustPercentValue(baselineCfg.slot_w, activeAdjustments.w)
            : baselineCfg.slot_w;
          let slotH = activeAdjustments
            ? this.adjustPercentValue(baselineCfg.slot_h, activeAdjustments.h)
            : baselineCfg.slot_h;
          let slotX = activeAdjustments
            ? this.adjustPercentValue(baselineCfg.slot_x, activeAdjustments.x)
            : baselineCfg.slot_x;
          let slotY = activeAdjustments
            ? this.adjustPercentValue(baselineCfg.slot_y, activeAdjustments.y)
            : baselineCfg.slot_y;
          const isVerticalTwoThreeNoFrame =
            orientation === 'vertical-2-3' || frameFamilyOrientation === 'vertical-2-3';
          if (orientation === 'horizontal-3-2' && normalizedSize === 'x-small') {
            slotW = this.adjustPercentValue(slotW, 3.0);
            slotH = this.adjustPercentValue(slotH, 3.0);
            slotX = this.adjustPercentValue(slotX, -0.2);
            slotY = this.adjustPercentValue(slotY, -0.1);
          } else if (isVerticalTwoThreeNoFrame && normalizedSize === 'x-small') {
            slotW = this.adjustPercentValue(slotW, 20.0);
            slotH = this.adjustPercentValue(slotH, 20.0);
            slotX = this.adjustPercentValue(slotX, -1.9);
            slotY = this.adjustPercentValue(slotY, 1.9);
          } else if (isVerticalTwoThreeNoFrame && normalizedSize === 'small') {
            slotW = this.adjustPercentValue(slotW, 21.5);
            slotH = this.adjustPercentValue(slotH, 21.5);
            slotX = this.adjustPercentValue(slotX, -1.9);
            slotY = this.adjustPercentValue(slotY, 1.6);
          } else if (isVerticalTwoThreeNoFrame && normalizedSize === 'medium') {
            slotW = this.adjustPercentValue(slotW, 18.2);
            slotH = this.adjustPercentValue(slotH, 18.2);
            slotX = this.adjustPercentValue(slotX, -1.6);
            slotY = this.adjustPercentValue(slotY, 1.8);
          } else if (isVerticalTwoThreeNoFrame && normalizedSize === 'large') {
            slotW = this.adjustPercentValue(slotW, 4.5);
            slotH = this.adjustPercentValue(slotH, 4.5);
            slotX = this.adjustPercentValue(slotX, -1.8);
            slotY = this.adjustPercentValue(slotY, 1.8);
          } else if (isVerticalTwoThreeNoFrame && normalizedSize === 'x-large') {
            slotW = this.adjustPercentValue(slotW, 13.2);
            slotH = this.adjustPercentValue(slotH, 13.2);
            slotX = this.adjustPercentValue(slotX, -1.7);
            slotY = this.adjustPercentValue(slotY, 2.0);
          } else if (isVerticalTwoThreeNoFrame && normalizedSize === 'collector') {
            slotW = this.adjustPercentValue(slotW, 11.2);
            slotH = this.adjustPercentValue(slotH, 11.2);
            slotX = this.adjustPercentValue(slotX, -1.0);
            slotY = this.adjustPercentValue(slotY, 1.0);
          } else if (orientation === 'horizontal-3-2' && normalizedSize === 'small') {
            slotW = this.adjustPercentValue(slotW, 3.2);
            slotH = this.adjustPercentValue(slotH, 3.2);
            slotX = this.adjustPercentValue(slotX, -0.1);
            slotY = this.adjustPercentValue(slotY, -0.1);
          } else if (orientation === 'horizontal-3-2' && normalizedSize === 'medium') {
            slotW = this.adjustPercentValue(slotW, 3.4);
            slotH = this.adjustPercentValue(slotH, 3.4);
            slotX = this.adjustPercentValue(slotX, -0.1);
            slotY = this.adjustPercentValue(slotY, -0.1);
          } else if (orientation === 'horizontal-3-2' && normalizedSize === 'large') {
            slotW = this.adjustPercentValue(slotW, 2.4);
            slotH = this.adjustPercentValue(slotH, 2.4);
            slotX = this.adjustPercentValue(slotX, -0.1);
            slotY = this.adjustPercentValue(slotY, -0.1);
          } else if (orientation === 'horizontal-3-2' && normalizedSize === 'x-large') {
            slotW = this.adjustPercentValue(slotW, 2.8);
            slotH = this.adjustPercentValue(slotH, 2.8);
            slotX = this.adjustPercentValue(slotX, -0.15);
            slotY = this.adjustPercentValue(slotY, -0.1);
          }
          this._slot.style.setProperty('--artwork-slot-w', slotW || this._slotDefaults.w);
          this._slot.style.setProperty('--artwork-slot-h', slotH || this._slotDefaults.h);
          this._slot.style.setProperty('--artwork-slot-x', slotX || this._slotDefaults.x);
          this._slot.style.setProperty('--artwork-slot-y', slotY || this._slotDefaults.y);
          let imageScale =
            baselineCfg.image_scale || baselineCfg.art_scale || this._slotDefaults.scale;
          if (orientation === 'horizontal-3-2' && normalizedSize === 'collector') {
            const base = parseFloat(imageScale);
            if (Number.isFinite(base)) imageScale = String(Math.round((base + 0.04) * 1000) / 1000);
          } else if (orientation === 'horizontal-3-2' && normalizedSize === 'x-small') {
            const base = parseFloat(imageScale);
            if (Number.isFinite(base)) imageScale = String(Math.round((base + 0.05) * 1000) / 1000);
          } else if (orientation === 'horizontal-3-2' && normalizedSize === 'small') {
            const base = parseFloat(imageScale);
            if (Number.isFinite(base)) imageScale = String(Math.round((base + 0.06) * 1000) / 1000);
          } else if (orientation === 'horizontal-3-2' && normalizedSize === 'medium') {
            const base = parseFloat(imageScale);
            if (Number.isFinite(base)) imageScale = String(Math.round((base + 0.07) * 1000) / 1000);
          } else if (orientation === 'horizontal-3-2' && normalizedSize === 'large') {
            const base = parseFloat(imageScale);
            if (Number.isFinite(base)) imageScale = String(Math.round((base + 0.04) * 1000) / 1000);
          } else if (orientation === 'horizontal-3-2' && normalizedSize === 'x-large') {
            const base = parseFloat(imageScale);
            if (Number.isFinite(base)) imageScale = String(Math.round((base + 0.06) * 1000) / 1000);
          }
          if (orientation === 'square' && normalizedSize === 'large') {
            const base = parseFloat(imageScale);
            if (Number.isFinite(base)) {
              /* No-frame square Large: baseline matches border frame mat window — scale down so the print reads smaller in the reveal. */
              imageScale = String(Math.round(base * 0.87 * 1000) / 1000);
            }
          }
          this._slot.style.setProperty('--artwork-image-scale', imageScale);
          return overlaySwapPromise;
        }

        // Safe fallback when a matching baseline frame config is unavailable.
        let slotW = '66%';
        let slotH = '94%';
        if (orientation === 'horizontal') {
          slotW = '94%';
          slotH = '66%';
        } else if (orientation === 'square') {
          slotW = '84%';
          slotH = '84%';
        }

        this._slot.style.setProperty('--artwork-slot-w', slotW);
        this._slot.style.setProperty('--artwork-slot-h', slotH);
        this._slot.style.setProperty('--artwork-slot-x', '50%');
        this._slot.style.setProperty('--artwork-slot-y', '50%');
        this._slot.style.setProperty('--artwork-image-scale', '1');
        return overlaySwapPromise;
      }

      pickNoFrameOrientation(candidates) {
        for (const candidate of candidates) {
          const normalized = this.normalizeOrientationToken(candidate);
          if (normalized) return normalized;
        }
        return 'vertical';
      }

      getNoFrameBaselineSlotConfig(orientation, size) {
        const frameEntries = Object.entries(this._frames || {});
        if (!frameEntries.length) return null;

        const normalizedOrientation = this.normalizeOrientationToken(orientation || 'vertical') || 'vertical';
        const normalizedSize = this.normalizeSizeToken(size || 'collector') || 'collector';
        const targetPrefix = `${normalizedOrientation}-`;
        const targetSuffix = `-${normalizedSize}`;

        const preferredMatchers = [
          // Best baseline: white border frame at same orientation/size.
          (key) => key.startsWith(targetPrefix) && key.includes('-white-border-') && key.endsWith(targetSuffix),
          // Then any border frame at same orientation/size.
          (key) => key.startsWith(targetPrefix) && key.includes('-border-') && key.endsWith(targetSuffix),
          // Then white full-bleed at same orientation/size.
          (key) => key.startsWith(targetPrefix) && key.includes('-white-full-bleed-') && key.endsWith(targetSuffix),
          // Then any full-bleed at same orientation/size.
          (key) => key.startsWith(targetPrefix) && key.includes('-full-bleed-') && key.endsWith(targetSuffix),
          // Fallback to any same orientation + size.
          (key) => key.startsWith(targetPrefix) && key.endsWith(targetSuffix),
          // Last resort: same orientation collector.
          (key) => key.startsWith(targetPrefix) && key.endsWith('-collector'),
        ];

        for (const matcher of preferredMatchers) {
          const hit = frameEntries.find(([key]) => matcher(key));
          if (!hit) continue;
          const [, cfg] = hit;
          if (cfg && typeof cfg === 'object') return cfg;
        }

        return null;
      }

      adjustPercentValue(value, delta) {
        const raw = (value || '').toString().trim();
        if (!raw.endsWith('%')) return raw || '';
        const numeric = parseFloat(raw);
        if (Number.isNaN(numeric)) return raw;
        const adjusted = Math.max(0, numeric + Number(delta || 0));
        return `${adjusted}%`;
      }

      syncMockupPresentationScale() {
        const baseScale =
          typeof this._mockupSizeBoost === 'number' &&
          Number.isFinite(this._mockupSizeBoost) &&
          this._mockupSizeBoost > 0
            ? this._mockupSizeBoost
            : 1;
        const familyOrientation = this.extractFrameFamilyOrientationToken(
          this.dataset.frameFamilyOrientation || this.dataset.frameOrientation || ''
        );
        const adjustedScale =
          familyOrientation === 'horizontal-3-2'
            ? baseScale * 0.74
            : familyOrientation === 'vertical-2-3'
              ? baseScale * 0.8
              : baseScale;
        this._frameMount?.style.setProperty('--artwork-mockup-scale', String(adjustedScale));
      }

      buildNoFrameAssetUrlCandidates(orientation, size) {
        if (!this._assetsBase) return [];
        const cacheKey = `${this._assetsBase}|${this._frameAssetsVersion}|noframe|${orientation || ''}|${size || ''}`;
        const cached = this.getFromCappedCache(this._noFrameCandidateCache, cacheKey);
        if (cached) return cached;

        const primary = orientation || 'vertical';
        const orientationCandidates = [primary];
        // If we appended vertical after square, a missing square file would 404 and vertical_no_frame_*
        // would still load—showing the wrong mat. Only non-square orientations may fall back to vertical.
        if (primary !== 'square' && !orientationCandidates.includes('vertical')) {
          orientationCandidates.push('vertical');
        }

        const sizeCandidates = [size || 'collector'];
        if (size === 'x-large') sizeCandidates.push('xlarge');
        if (size === 'x-small') sizeCandidates.push('xsmall');

        const urls = [];
        const filePatterns = [
          '{orientation}_border_no_frame_{size}.webp',
          '{orientation}_no_frame_border_{size}.webp',
          '{orientation}_no_frame_{size}.webp',
          '{orientation}_print_only_{size}.webp',
          '{orientation}_unframed_{size}.webp',
        ];
        orientationCandidates.forEach((orientationToken) => {
          sizeCandidates.forEach((sizeToken) => {
            filePatterns.forEach((pattern) => {
              const fileName = pattern
                .replace('{orientation}', orientationToken)
                .replace('{size}', sizeToken);
              urls.push(this.withFrameAssetVersion(`${this._assetsBase}${fileName}`));
            });
          });
        });

        const deduped = [...new Set(urls)];
        this.setInCappedCache(this._noFrameCandidateCache, cacheKey, deduped, this._noFrameCandidateCacheMax);
        return deduped;
      }

      async setOverlayNoFrameFromCandidates(overlay, candidates) {
        const filteredCandidates = [...new Set((candidates || []).filter(Boolean))];
        if (!filteredCandidates.length) {
          overlay.style.opacity = '0';
          return;
        }

        const swapToken = ++this._overlaySwapToken;
        for (const candidate of filteredCandidates) {
          const loadedSrc = await this.preloadImage(candidate);
          if (swapToken !== this._overlaySwapToken) return;
          if (!loadedSrc) continue;
          if (overlay.getAttribute('src') !== candidate) overlay.setAttribute('src', candidate);
          overlay.style.opacity = '1';
          return;
        }

        if (swapToken !== this._overlaySwapToken) return;
        overlay.style.opacity = '0';
      }

      extractSizeHint(frameKey) {
        const key = (frameKey || '').toLowerCase();
        const knownSizes = ['x-small', 'x-large', 'small', 'medium', 'large', 'collector'];
        const hit = knownSizes.find((size) => key.endsWith(`-${size}`) || key.includes(`-${size}-`));
        return hit || '';
      }

      extractColorHint(frameKey) {
        const key = (frameKey || '').toLowerCase();
        const knownColors = ['white', 'black', 'wood', 'natural-wood'];
        return knownColors.find((color) => key.includes(`-${color}-`)) || '';
      }

      extractOrientationHint(frameKey) {
        const key = (frameKey || '').toLowerCase();
        const known = ['horizontal-3-2', 'vertical-2-3', 'vertical', 'horizontal', 'square'];
        return known.find((item) => key.startsWith(`${item}-`) || key.includes(`-${item}-`)) || '';
      }

      extractMountHint(frameKey) {
        const key = (frameKey || '').toLowerCase();
        if (key.includes('-full-bleed-')) return 'full-bleed';
        if (key.includes('-border-')) return 'border';
        return '';
      }

      replaceMountInFrameKey(frameKey, mountToken) {
        const key = (frameKey || '').toString();
        if (!key || !mountToken) return '';
        if (key.includes('-full-bleed-')) return key.replace('-full-bleed-', `-${mountToken}-`);
        if (key.includes('-border-')) return key.replace('-border-', `-${mountToken}-`);
        return '';
      }

      buildFrameKey(variant, row) {
        const knownSizes = ['x-small', 'x-large', 'small', 'medium', 'large', 'collector'];
        const values = [
          (row?.option1 ?? variant?.option1 ?? '').toString().toLowerCase(),
          (row?.option2 ?? variant?.option2 ?? '').toString().toLowerCase(),
          (row?.option3 ?? variant?.option3 ?? '').toString().toLowerCase(),
        ];

        const byName = {};
        (this._optionNames || []).forEach((name, i) => {
          const key = (name || '').toString().toLowerCase();
          if (key) byName[key] = values[i] || '';
        });

        const displayedImageOrientation = this.inferOrientationFromDisplayedImage();
        const forcedOrientation = this.normalizeOrientationToken(this.dataset.frameOrientation || '');
        const variantOrientation = this.normalizeOrientationToken(row?.artwork_orientation ?? variant?.artwork_orientation ?? '');
        const optionOrientation = this.normalizeOrientationToken(
          this.pickOptionValue(byName, ['orientation']) || this.findToken(values.join(' '), ['vertical', 'horizontal', 'square']) || ''
        );
        const mediaOrientation = this.inferOrientationFromVariantMedia(variant, row);
        const orientationCandidates = [];
        if (this.isCartLineArtworkViewer()) {
          if (displayedImageOrientation) orientationCandidates.push(displayedImageOrientation);
          if (mediaOrientation) orientationCandidates.push(mediaOrientation);
        }
        if (variantOrientation) orientationCandidates.push(variantOrientation);
        if (forcedOrientation === 'square') orientationCandidates.push('square');
        if (forcedOrientation === 'horizontal') orientationCandidates.push('horizontal');
        if (!this.isCartLineArtworkViewer()) {
          orientationCandidates.push(optionOrientation, mediaOrientation, displayedImageOrientation);
        } else {
          orientationCandidates.push(optionOrientation);
        }
        if (forcedOrientation === 'vertical') orientationCandidates.push('vertical');
        orientationCandidates.push('vertical');
        let orientation = this.pickNoFrameOrientation(orientationCandidates);
        const frameFamilyOrientationBlob = [
          this.dataset.frameFamilyOrientation,
          row?.artwork_orientation,
          variant?.artwork_orientation,
          this.pickOptionValue(byName, ['orientation']),
          values.join(' '),
        ]
          .filter(Boolean)
          .join(' ');
        const frameFamilyOrientation = this.extractFrameFamilyOrientationToken(frameFamilyOrientationBlob);
        /* Product PDP metafield (data-frame-orientation) first; then variant row; never force "vertical" from a bad per-variant import — that used to wipe Gold & Teal square. */
        if (forcedOrientation === 'square') {
          orientation = 'square';
        } else if (forcedOrientation === 'horizontal') {
          orientation = 'horizontal';
        } else if (variantOrientation === 'square') {
          orientation = 'square';
        } else if (variantOrientation === 'horizontal') {
          orientation = 'horizontal';
        }
        if (orientation === 'horizontal' && frameFamilyOrientation === 'horizontal-3-2') {
          orientation = 'horizontal-3-2';
        } else if (orientation === 'vertical' && frameFamilyOrientation === 'vertical-2-3') {
          orientation = 'vertical-2-3';
        }

        const frameKeys = Object.keys(this._frames || {});
        const orientationHasMappedFrames = (o) => {
          const n = this.normalizeOrientationToken(o);
          if (!n) return false;
          return frameKeys.some((key) => key.startsWith(`${n}-`));
        };
        const lockedOrientationFromMetafield =
          forcedOrientation === 'square' ||
          forcedOrientation === 'horizontal' ||
          forcedOrientation === 'vertical';
        if (
          !lockedOrientationFromMetafield &&
          !orientationHasMappedFrames(orientation)
        ) {
          orientation =
            this.pickFirstAvailableOrientation([
              orientation,
              variantOrientation,
              optionOrientation,
              mediaOrientation,
              displayedImageOrientation,
              'square',
              'horizontal',
              'vertical',
            ]) || orientation;
        }

        const forcedColor = this.slugify(this.dataset.frameColor || '');
        const forcedMount = this.slugify(this.dataset.frameMount || '');
        const cartLine = this.isCartLineArtworkViewer();
        const frameStyleMount = cartLine ? '' : this.getCurrentFrameStyleMountHint();
        const frameStyleMountFromValues = this.getFrameStyleMountHintFromOptionValues(byName, values);
        const uiMountSlug = this.normalizeMountValueToSlug(this.getCustomMountValue());
        const uiColor = cartLine ? '' : this.getCurrentFrameColorToken();
        /* Never use generic option names "color"/"colour" here — they usually mean print Colour vs B&W and slugify to "bw",
           which breaks cart frame keys (e.g. wood frame becomes vertical-bw-... and loads wrong / missing overlay). */
        const frameColorFromLabels = this.pickOptionValue(byName, ['frame color', 'frame colour']);
        let frameColorFromFrameOption = (byName.frame || '').toString().trim();
        if (
          frameColorFromFrameOption &&
          /gallery|signature|float|full[\s-]?bleed|border|print\s*only|no\s*frame|unframed/i.test(
            frameColorFromFrameOption
          )
        ) {
          frameColorFromFrameOption = '';
        }
        const frameColorFromTokens = this.findToken(values.join(' '), [
          'natural wood',
          'white',
          'black',
          'wood',
          'no frame',
        ]);
        const colorRaw = this.slugify(
          forcedColor ||
            uiColor ||
            frameColorFromLabels ||
            frameColorFromFrameOption ||
            frameColorFromTokens ||
            'white'
        );
        const color = colorRaw === 'natural-wood' ? 'wood' : colorRaw;
        const mount = this.slugify(
          forcedMount ||
            uiMountSlug ||
            this.pickOptionValue(byName, ['mount']) ||
            frameStyleMountFromValues ||
            frameStyleMount ||
            this.findToken(values.join(' '), ['full bleed', 'full-bleed', 'no bleed', 'no-bleed', 'nobleed', 'border']) ||
            'full-bleed'
        )
          .replace('full bleed', 'full-bleed')
          .replace('no bleed', 'full-bleed')
          .replace('no-bleed', 'full-bleed')
          .replace('nobleed', 'full-bleed');
        const rawSize =
          (cartLine ? '' : this.getCurrentSizeToken()) ||
          this.pickOptionValue(byName, ['size']) ||
          this.findToken(values.join(' '), knownSizes) ||
          'collector';
        const normalizedSize = this.findToken(rawSize, knownSizes) || rawSize;
        const size = this.slugify(normalizedSize);

        return `${orientation}-${color}-${mount}-${size}`;
      }

      getCustomMountValue() {
        const fromData = (this.dataset.cartMount || '').toString().trim();
        if (fromData) return fromData;

        if (this.isCartLineArtworkViewer()) {
          return '';
        }

        // Line-item Mount (properties) must win over any —mount-toggle fieldset so we never read artwork Color/B&W pills.
        const fromLineItemMount = this.getActiveCheckedInput('input[data-mount-option-input]:checked');
        if (fromLineItemMount) return (fromLineItemMount.value || '').toString();

        const fromVariantMount = this.getActiveCheckedInput(
          '.product-form__input--mount-toggle:not(.product-form__input--artwork-color-toggle) input[type="radio"]:checked'
        );
        return (fromVariantMount?.value || '').toString();
      }

      normalizeMountValueToSlug(raw) {
        const s = (raw || '').toString().toLowerCase().trim();
        if (!s) return '';
        if ((s.includes('full') && s.includes('bleed')) || (s.includes('no') && s.includes('bleed')) || s.includes('nobleed')) return 'full-bleed';
        if (s === 'border' || /\bborder\b/.test(s)) return 'border';
        return '';
      }

      getCurrentFrameStyleMountHint() {
        const checkedStyleInput = this.getActiveCheckedInput(
          ".product-form__input--frame-style-list input[type='radio']:checked"
        );
        if (!checkedStyleInput) return '';
        return this.inferMountFromFrameStyleText(checkedStyleInput.value || '');
      }

      getFrameStyleMountHintFromVariant(variant, row) {
        const values = [
          (row?.option1 ?? variant?.option1 ?? '').toString(),
          (row?.option2 ?? variant?.option2 ?? '').toString(),
          (row?.option3 ?? variant?.option3 ?? '').toString(),
        ].filter(Boolean);
        for (const value of values) {
          const inferred = this.inferMountFromFrameStyleText(value);
          if (inferred) return inferred;
        }
        return '';
      }

      getFrameStyleMountHintFromOptionValues(byName, values) {
        const frameStyleValue =
          this.pickOptionValue(byName, ['frame style']) ||
          (values || []).find((value) =>
            /(gallery|signature|float|full[\s-]?bleed|border|frame)/i.test(value || '')
          ) ||
          '';
        return this.inferMountFromFrameStyleText(frameStyleValue);
      }

      inferMountFromFrameStyleText(value) {
        const text = (value || '').toString().toLowerCase();
        if (!text) return '';
        if (text.includes('no frame') || text.includes('print only') || text.includes('unframed')) return '';
        if (text.includes('full bleed') || text.includes('full-bleed') || text.includes('no bleed') || text.includes('no-bleed')) return 'full-bleed';
        if (text.includes('float') || text.includes('signature')) return 'full-bleed';
        if (text.includes('border')) return 'border';
        if (text.includes('gallery') || text.includes('frame')) return 'border';
        return '';
      }

      matchesSignatureFloatLabel(raw) {
        const t = (raw || '').toString().toLowerCase();
        if (!t) return false;
        if (/\bsignature\b/.test(t) && /\bfloat\b/.test(t)) return true;
        if (t.includes('signature float')) return true;
        return false;
      }

      /** Works for vertical, horizontal & square PDPs — uses checked frame-style control, then variant matrix. */
      isSignatureFloatFrameSelected(variant, row) {
        if (!this.isCartLineArtworkViewer()) {
          const checked = this.getActiveCheckedInput(
            ".product-form__input--frame-style-list input[type='radio']:checked"
          );
          if (this.matchesSignatureFloatLabel(checked?.value)) return true;
        }

        const values = [
          (row?.option1 ?? variant?.option1 ?? '').toString(),
          (row?.option2 ?? variant?.option2 ?? '').toString(),
          (row?.option3 ?? variant?.option3 ?? '').toString(),
        ];

        const byName = {};
        (this._optionNames || []).forEach((name, i) => {
          const key = (name || '').toString().toLowerCase();
          if (key) byName[key] = values[i] ?? '';
        });
        const labelledStyle =
          this.pickOptionValue(byName, ['frame style']) ||
          (values || []).find((value) => /gallery|signature|float|shadow|frame|print only|no\s*frame/i.test(value || ''));

        if (this.matchesSignatureFloatLabel(labelledStyle)) return true;

        const blob = values.join(' ').toLowerCase();
        return /\bsignature\b/.test(blob) && /\bfloat\b/.test(blob);
      }

      findMountBorderAndFullBleed(fieldset) {
        const mountRadios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
        const borderRadio = mountRadios.find((r) => /\bborder\b/i.test(String(r.value || '').trim()));
        const fullBleedRadio = mountRadios.find((r) => {
          const vv = String(r.value || '').toLowerCase().trim();
          return vv.includes('full') && vv.includes('bleed');
        });
        return { borderRadio, fullBleedRadio, mountRadios };
      }

      syncMountOptionState(noFrameLocksMountChoice, signatureFloatLocksFullBleedOnly) {
        const root = this._rootContext || this;
        const mountFieldsets = root.querySelectorAll('.product-form__input--mount-toggle');
        mountFieldsets.forEach((fieldset) => {
          const { borderRadio, fullBleedRadio, mountRadios } = this.findMountBorderAndFullBleed(fieldset);
          mountRadios.forEach((input) => {
            input.disabled = false;
            input.removeAttribute('aria-disabled');
          });
          fieldset.classList.toggle('signature-float-mount-lock', false);
          fieldset.classList.remove('is-disabled');
          fieldset.setAttribute('aria-disabled', 'false');

          if (noFrameLocksMountChoice) {
            if (fullBleedRadio) fullBleedRadio.disabled = true;
            if (borderRadio) borderRadio.checked = true;
            return;
          }

          if (signatureFloatLocksFullBleedOnly) {
            fieldset.classList.toggle('signature-float-mount-lock', true);
            if (borderRadio) {
              borderRadio.disabled = true;
              borderRadio.setAttribute('aria-disabled', 'true');
            }
            if (fullBleedRadio) {
              fullBleedRadio.disabled = false;
              if (!fullBleedRadio.checked) {
                fullBleedRadio.checked = true;
                queueMicrotask(() =>
                  fullBleedRadio.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }))
                );
              }
            }
            return;
          }
        });
      }

      getCurrentFrameColorToken() {
        const checkedColorInput = this.getActiveCheckedInput(
          ".product-form__input--frame-swatches input[type='radio']:checked"
        );
        if (!checkedColorInput) return '';
        const raw = (checkedColorInput.value || '').toString().toLowerCase();
        if (raw.includes('black')) return 'black';
        if (raw.includes('white')) return 'white';
        if (raw.includes('wood') || raw.includes('oak') || raw.includes('walnut')) return 'wood';
        return this.slugify(raw);
      }

      preloadFrameImages() {
        const frameConfigs = Object.values(this._frames || {});
        frameConfigs.forEach((cfg) => {
          const src = typeof cfg === 'string' ? cfg : cfg?.src;
          if (typeof src === 'string' && src) {
            this.preloadImage(src);
          }
        });
      }

      preloadInitialFrameImage() {
        const src = this._overlay?.getAttribute('src') || this._fallbackOverlaySrc || '';
        if (!src) return;
        this.preloadImage(src);
      }

      preloadImage(src) {
        if (!src) return Promise.resolve();
        if (this._frameLoadCache.has(src)) return this._frameLoadCache.get(src);

        const promise = new Promise((resolve) => {
          const img = new Image();
          img.decoding = 'async';
          img.onload = () => resolve(src);
          img.onerror = () => resolve(null);
          img.src = src;
        });

        this._frameLoadCache.set(src, promise);
        return promise;
      }

      buildFrameAssetUrlCandidates(frameKey) {
        if (!this._assetsBase) return [];
        const cacheKey = `${this._assetsBase}|${this._frameAssetsVersion}|frame|${frameKey || ''}`;
        const cached = this.getFromCappedCache(this._frameCandidateCache, cacheKey);
        if (cached) return cached;

        const orientation = this.extractOrientationHint(frameKey);
        const color = this.extractColorHint(frameKey);
        const mount = this.extractMountHint(frameKey);
        const size = this.extractSizeHint(frameKey);
        if (!orientation || !color || !mount || !size) return [];

        // Fast path for new ratio families: avoid large fallback candidate lists that cause
        // sequential 404 waits and sluggish size switching in fresh sessions.
        if (orientation === 'horizontal-3-2' || orientation === 'vertical-2-3') {
          const sizeCandidates = [size];
          if (size === 'x-large') sizeCandidates.push('xlarge');
          if (size === 'x-small') sizeCandidates.push('xsmall');
          const mountCandidates = [mount];
          if (mount === 'full-bleed') mountCandidates.push('full_bleed');

          let colorCandidates;
          if (color === 'wood' || color === 'natural-wood') {
            colorCandidates = ['natural_wood', 'wood'];
          } else {
            colorCandidates = [color];
          }

          const urls = [];
          colorCandidates.forEach((colorToken) => {
            sizeCandidates.forEach((sizeToken) => {
              mountCandidates.forEach((mountToken) => {
                urls.push(
                  this.withFrameAssetVersion(
                    `${this._assetsBase}${orientation}_${colorToken}_${mountToken}_${sizeToken}.webp`
                  )
                );
              });
            });
          });

          const dedupedFast = [...new Set(urls)];
          this.setInCappedCache(this._frameCandidateCache, cacheKey, dedupedFast, this._frameCandidateCacheMax);
          return dedupedFast;
        }

        // Border + square full-bleed assets use `natural_wood` in filenames; h/v full-bleed use `wood` first.
        const isWood = color === 'wood' || color === 'natural-wood';
        let colorCandidates;
        if (isWood) {
          if (mount === 'border') {
            colorCandidates = ['natural_wood', 'natural-wood', 'wood'];
          } else if (mount === 'full-bleed' && orientation === 'square') {
            colorCandidates = ['natural_wood', 'natural-wood', 'wood'];
          } else if (mount === 'full-bleed') {
            colorCandidates = ['wood', 'natural-wood', 'natural_wood'];
          } else {
            colorCandidates = ['natural_wood', 'natural-wood', 'wood'];
          }
        } else {
          colorCandidates = [color];
        }

        const sizeCandidates = [size];
        if (size === 'x-large') sizeCandidates.push('xlarge');
        if (size === 'x-small') sizeCandidates.push('xsmall');
        const mountCandidates = [mount];
        if (mount === 'full-bleed') mountCandidates.push('full_bleed');

        const orientationCandidates = [orientation];
        /* Do not try vertical/horizontal URLs for square keys: vertical wood border assets often exist and
           would load when a square asset 404s, showing the wrong frame shape on square PDPs. */
        const urls = [];
        colorCandidates.forEach((colorToken) => {
          const colorFilenameCandidates = [colorToken, colorToken.replace(/-/g, '_')];
          sizeCandidates.forEach((sizeToken) => {
            colorFilenameCandidates.forEach((colorFilenameToken) => {
              mountCandidates.forEach((mountToken) => {
                orientationCandidates.forEach((orientationToken) => {
                  urls.push(
                    this.withFrameAssetVersion(
                      `${this._assetsBase}${orientationToken}_${colorFilenameToken}_${mountToken}_${sizeToken}.webp`
                    )
                  );
                  // Support legacy/irregular asset names that use dashes between prefix segments.
                  urls.push(
                    this.withFrameAssetVersion(
                      `${this._assetsBase}${orientationToken}-${colorFilenameToken}-${mountToken}_${sizeToken}.webp`
                    )
                  );
                  urls.push(
                    this.withFrameAssetVersion(
                      `${this._assetsBase}${orientationToken}-${colorFilenameToken}-${mountToken}-${sizeToken}.webp`
                    )
                  );
                  // Support accidental filenames committed with a trailing space before extension.
                  urls.push(
                    this.withFrameAssetVersion(
                      `${this._assetsBase}${orientationToken}_${colorFilenameToken}_${mountToken}_${sizeToken} .webp`
                    )
                  );
                });
              });
            });
          });
        });

        const deduped = [...new Set(urls)];
        this.setInCappedCache(this._frameCandidateCache, cacheKey, deduped, this._frameCandidateCacheMax);
        return deduped;
      }

      getFromCappedCache(map, key) {
        if (!map || !map.has(key)) return null;
        const value = map.get(key);
        // Promote to most-recently-used
        map.delete(key);
        map.set(key, value);
        return value;
      }

      setInCappedCache(map, key, value, maxEntries) {
        if (!map) return;
        if (map.has(key)) map.delete(key);
        map.set(key, value);
        const cap = Number.isFinite(maxEntries) && maxEntries > 0 ? maxEntries : 200;
        while (map.size > cap) {
          const oldestKey = map.keys().next().value;
          if (oldestKey == null) break;
          map.delete(oldestKey);
        }
      }

      preloadLikelyAdjacentFrameAssets({ orientation, color, mount, size }) {
        if (!orientation || !color || !mount || !size) return;
        const adjacentSizes = this.getAdjacentSizeTokens(size);
        if (!adjacentSizes.length) return;

        adjacentSizes.forEach((adjacentSize) => {
          const key = `${orientation}-${color}-${mount}-${adjacentSize}`;
          const candidates = this.buildFrameAssetUrlCandidates(key);
          const preloadCount =
            orientation === 'horizontal-3-2' || orientation === 'vertical-2-3' ? 2 : 8;
          candidates.slice(0, preloadCount).forEach((src) => this.preloadImage(src));
        });
      }

      preloadLikelyAdjacentNoFrameAssets(orientation, size) {
        if (!orientation || !size) return;
        const adjacentSizes = this.getAdjacentSizeTokens(size);
        if (!adjacentSizes.length) return;

        adjacentSizes.forEach((adjacentSize) => {
          const candidates = this.buildNoFrameAssetUrlCandidates(orientation, adjacentSize);
          candidates.slice(0, 4).forEach((src) => this.preloadImage(src));
        });
      }

      async setOverlaySrcFromCandidates(overlay, candidates) {
        const filteredCandidates = [...new Set((candidates || []).filter(Boolean))];
        if (!filteredCandidates.length) {
          const fallbackSrc = this._fallbackOverlaySrc || overlay.getAttribute('src') || '';
          if (fallbackSrc) {
            if (overlay.getAttribute('src') !== fallbackSrc) overlay.setAttribute('src', fallbackSrc);
            overlay.style.opacity = '1';
          } else {
            overlay.style.opacity = '0';
          }
          return;
        }

        const swapToken = ++this._overlaySwapToken;
        for (const candidate of filteredCandidates) {
          const loadedSrc = await this.preloadImage(candidate);
          if (swapToken !== this._overlaySwapToken) return;
          if (!loadedSrc) continue;
          if (overlay.getAttribute('src') !== candidate) overlay.setAttribute('src', candidate);
          this._fallbackOverlaySrc = candidate;
          overlay.style.opacity = '1';
          return;
        }

        if (swapToken !== this._overlaySwapToken) return;
        const fallbackSrc = this._fallbackOverlaySrc || overlay.getAttribute('src') || '';
        if (fallbackSrc) {
          if (overlay.getAttribute('src') !== fallbackSrc) overlay.setAttribute('src', fallbackSrc);
          overlay.style.opacity = '1';
        } else {
          overlay.style.opacity = '0';
        }
      }

      async setOverlaySrcWhenReady(overlay, nextSrc) {
        const currentSrc = overlay.getAttribute('src') || '';
        if (currentSrc === nextSrc) {
          overlay.style.opacity = '1';
          return;
        }

        const swapToken = ++this._overlaySwapToken;
        await this.preloadImage(nextSrc);
        if (swapToken !== this._overlaySwapToken) return;
        if (overlay.getAttribute('src') !== nextSrc) {
          overlay.setAttribute('src', nextSrc);
        }
        overlay.style.opacity = '1';
      }

      withFrameAssetVersion(url) {
        if (!url || typeof url !== 'string') return url;
        if (!this._frameAssetsVersion) return url;
        if (!url.includes('/assets/')) return url;
        if (url.includes('?')) return url;
        return `${url}?v=${encodeURIComponent(this._frameAssetsVersion)}`;
      }

      getSizeTokenForUnframed(variant, row, optionsBlob, knownSizes) {
        const fromList = this.getCurrentSizeToken();
        if (fromList) return fromList;
        const fromFieldset = this.getCheckedValueFromPillFieldsetByLegend(/size|dimension|format|print/i, /frame\s*style|swatch|colour|color|mount/i);
        if (fromFieldset) {
          const t = this.normalizeSizeToken(fromFieldset);
          if (t) return t;
        }
        const byName = {};
        (this._optionNames || []).forEach((name, i) => {
          const key = (name || '').toString().toLowerCase();
          if (!key) return;
          const v = (row?.[`option${i + 1}`] ?? variant?.[`option${i + 1}`] ?? '')?.toString() || '';
          byName[key] = v;
        });
        const fromNamed = this.pickOptionValue(byName, [
          'size',
          'print size',
          'artwork size',
          'dimensions',
          'format',
        ]);
        if (fromNamed) {
          const t = this.normalizeSizeToken(fromNamed);
          if (t) return t;
        }
        const fromBlob = this.findToken(optionsBlob, knownSizes);
        if (fromBlob) return fromBlob;
        const combined = [variant?.option1, variant?.option2, variant?.option3]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return this.findToken(combined, knownSizes) || '';
      }

      getCheckedValueFromPillFieldsetByLegend(matchLegend, skipLegend) {
        const root = this._rootContext || this;
        const fieldsets = root.querySelectorAll('fieldset.product-form__input--pill');
        for (const fs of fieldsets) {
          if (fs.classList.contains('product-form__input--frame-style-list') || fs.classList.contains('product-form__input--mount-toggle')) {
            continue;
          }
          const leg = (fs.querySelector('legend')?.textContent || '').toLowerCase();
          if (skipLegend.test(leg)) continue;
          if (!matchLegend.test(leg)) continue;
          const input = fs.querySelector("input[type='radio']:checked");
          if (input && this.isElementVisible(input)) {
            return (input.value || '').toString();
          }
        }
        return '';
      }

      getCurrentSizeToken() {
        const checkedSizeInput = this.getActiveCheckedInput(
          ".product-form__input--size-list input[type='radio']:checked"
        );
        if (!checkedSizeInput) return this.normalizeSizeToken(this.getCheckedValueFromPillFieldsetByLegend(/size|dimension|format|print/i, /frame\s*style|swatch|colour|color|mount/i) || '') || '';
        return this.normalizeSizeToken(checkedSizeInput.value || '');
      }

      getActiveCheckedInput(selector) {
        const root = this._rootContext || this;
        const matches = Array.from(root.querySelectorAll(selector));
        if (!matches.length) return null;
        return matches.find((input) => this.isElementVisible(input)) || matches[0];
      }

      isElementVisible(element) {
        if (!element || !(element instanceof Element)) return false;
        if (element.getClientRects().length === 0) return false;
        const style = window.getComputedStyle(element);
        return style.display !== 'none' && style.visibility !== 'hidden';
      }

      normalizeSizeToken(value) {
        const raw = (value || '').toString().toLowerCase().trim();
        const exact = { xs: 'x-small', s: 'small', sm: 'small', m: 'medium', l: 'large', xl: 'x-large' };
        if (exact[raw]) return exact[raw];
        const text = raw;
        if (/\bextra[\s-]?small\b/i.test(text) || /\bx[\s-]?s(?:mall)?\b/i.test(text) || /\bxs\b/i.test(text)) {
          return 'x-small';
        }
        const knownSizes = ['x-small', 'x-large', 'small', 'medium', 'large', 'collector'];
        return this.findToken(text, knownSizes) || this.slugify(text);
      }

      syncDataFrameOrientationAttribute(orientationToken) {
        const o = this.normalizeOrientationToken(orientationToken);
        if (!o) return;
        const cur = this.getAttribute('data-frame-orientation');
        if (cur !== o) this.setAttribute('data-frame-orientation', o);
      }

      normalizeOrientationToken(value) {
        const text = (value || '').toString().toLowerCase();
        if (!text) return '';
        if (text.includes('horiz') || text.includes('landscape') || text.includes('horiztonal')) return 'horizontal';
        if (text.includes('square')) return 'square';
        if (text.includes('vert') || text.includes('portrait')) return 'vertical';
        if (text.includes('3:2') || text.includes('3 / 2') || text.includes('3-2')) return 'horizontal';
        if (text.includes('2:3') || text.includes('2 / 3') || text.includes('2-3')) return 'vertical';
        if (text.includes('1:1') || text.includes('1 / 1')) return 'square';
        return this.slugify(text);
      }

      extractFrameFamilyOrientationToken(value) {
        const text = (value || '').toString().toLowerCase();
        if (!text) return '';
        if (text.includes('3:2') || text.includes('3 / 2') || text.includes('3-2')) return 'horizontal-3-2';
        if (text.includes('2:3') || text.includes('2 / 3') || text.includes('2-3')) return 'vertical-2-3';
        return this.normalizeOrientationToken(text);
      }

      inferOrientationFromVariantMedia(variant, row) {
        let width = Number(variant?.featured_media?.preview_image?.width || 0);
        let height = Number(variant?.featured_media?.preview_image?.height || 0);
        const r = row || {};
        if (!width || !height) {
          width = Number(r.featured_w || variant?.featured_w || variant?.featuredW || 0);
          height = Number(r.featured_h || variant?.featured_h || variant?.featuredH || 0);
        }
        if (!width || !height) return '';
        if (width === height) return 'square';
        return width > height ? 'horizontal' : 'vertical';
      }

      inferOrientationFromDisplayedImage() {
        const image = this._imageEl || this.querySelector('.product-artwork-viewer__image');
        if (!image) return '';
        const width = Number(image.naturalWidth || image.width || 0);
        const height = Number(image.naturalHeight || image.height || 0);
        if (!width || !height) return '';
        if (width === height) return 'square';
        return width > height ? 'horizontal' : 'vertical';
      }

      pickFirstAvailableOrientation(candidates) {
        const frameKeys = Object.keys(this._frames || {});
        let firstValid = '';
        for (const candidate of candidates) {
          const normalized = this.normalizeOrientationToken(candidate);
          if (!normalized) continue;
          if (!firstValid) firstValid = normalized;
          if (frameKeys.some((key) => key.startsWith(`${normalized}-`))) return normalized;
        }
        return firstValid || 'vertical';
      }

      parseFrames(framesEl) {
        if (!framesEl) return {};
        const raw = JSON.parse(framesEl.textContent || '{}');
        const out = {};
        const list = Array.isArray(raw?.frames) ? raw.frames : [];
        list.forEach((entry) => {
          if (!entry || typeof entry !== 'object') return;
          Object.entries(entry).forEach(([key, value]) => {
            if (!key || !value) return;
            out[this.slugify(key)] = value;
          });
        });
        return out;
      }

      pickOptionValue(byName, needles) {
        const keys = Object.keys(byName);
        for (const needle of needles) {
          const hit = keys.find((k) => k.includes(needle));
          if (hit && byName[hit]) return byName[hit];
        }
        return '';
      }

      findToken(blob, tokens) {
        for (const token of tokens) {
          const pattern = token.replace('-', '[\\s-]?');
          if (new RegExp(`\\b${pattern}\\b`, 'i').test(blob)) return token;
        }
        return '';
      }

      slugify(value) {
        return (value || '')
          .toString()
          .trim()
          .toLowerCase()
          .replace(/[()]/g, '')
          .replace(/\s+/g, '-')
          .replace(/-+/g, '-')
          .replace(/^-|-$/g, '');
      }
    }
  );
}
